import Tippy from '@tippyjs/react';
import { useChangeImageName, useDeleteImage, useBatchImages } from 'app/api/documents/image-queries';
import request from 'app/api/request';
import { useCss } from 'app/api/style-queries';
import { ConfirmationModal, DataImage, FileZone, Modal, Spinner, TippyMenu, ToolMenu } from 'app/components';
import { formatDate } from 'app/utils/date';
import { i18n } from 'app/utils/i18n';
import { filesize } from 'filesize';
import path from 'path';
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { followCursor } from 'tippy.js';
import { useSelector } from 'react-redux';
import { useCreateAccessLog } from 'app/api/useAccessLogs';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { dispatchMessage, EventType } from 'app/useToasts';
import ServerErrorHandler from 'app/ErrorHandler';
import { useQueryClient } from 'react-query';
import { usePermissions } from 'app/utils/hooks/document-permission';
import { useGetDocumentBySuperId, queryKeys as documentQueryKeys } from 'app/api/documents/document-queries';
const Picture = ({ listType, imageData, imageMenuItems, documentSuperId }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const sectionPermissions = useSelector((state) => state.document.sectionPermissions);
  const { mutate: createAccessLog } = useCreateAccessLog();
  const { pDocument } = useEditorSelector((editor) => editor);

  if (!imageData) {
    return <Spinner />;
  }

  const data = imageData;
  function renderBoxPicture() {
    return (
      <Tippy
        interactive
        offset={[110, -20]}
        followCursor="initial"
        trigger="click"
        plugins={[followCursor]}
        content={<ToolMenu menuItems={imageMenuItems(data)} />}
        onShow={() => setTooltipVisible(true)}
        onHide={() => setTooltipVisible(false)}
      >
        <div className={`picture ${tooltipVisible ? 'active' : ''}`}>
          <div className="img-wrapper">
            <DataImage {...data.file} />
          </div>
          <div className="file-name">{data.file.filename}</div>
        </div>
      </Tippy>
    );
  }

  function renderListPicture() {
    return (
      <tr>
        <td className="image-cell">
          <DataImage {...data.file} />
        </td>
        <td className="text-center">{formatImageOccurences(data.occurences, documentSuperId, sectionPermissions, createAccessLog, pDocument.organization)}</td>
        <td>{data.file.filename}</td>
        <td className={data.file.fileContent?.length > 1000000 ? 'text-danger' : ''}>{sizeWithVolume(data.metadata.size ?? 0)}</td>
        <td>{formatDate(data.file.createdAt)}</td>
        <td>
          <TippyMenu menu={imageMenuItems(data)} />
        </td>
      </tr>
    );
  }

  switch (listType) {
    case 'box':
      return renderBoxPicture();
    case 'list':
    default:
      return renderListPicture();
  }
};

function sizeWithVolume(size) {
  return filesize(size, { base: 10, round: 1 });
}

const formatImageOccurences = (occurences, documentId, sectionPermissions, createAccessLog, organizationId) => {
  if (!occurences || occurences.length === 0) {
    return <span>-</span>;
  }
  return occurences.map((occurence, index) => {
    const hasPermission = sectionPermissions[occurence.sectionSuperId];
    return (
      <div key={index} className="occurences">
        <span>
          {hasPermission ? (
            <Link
              to={`/documents/${documentId}/sections/${occurence.sectionSuperId}#${occurence?.pageId}`}
              className="occurence-item"
              onClick={() => createAccessLog({ sectionSuperId: occurence.sectionSuperId, documentSuperId: documentId, organizationId })}
            >
              {i18n('pictures.list-selector.list.thead.page') + ' ' + occurence.pageIndex}
            </Link>
          ) : (
            <Tippy className="gaby-tooltip" content={<p>{i18n('pictures.list-selector.list.thead.no-access')}</p>}>
              <p className="disabled-occurence-item">{i18n('pictures.list-selector.list.thead.page') + ' ' + occurence.pageIndex}</p>
            </Tippy>
          )}
        </span>
      </div>
    );
  });
};

const UploadFile = ({ old, onSuccess, documentId }) => {
  return (
    <div className="add-new-popup">
      <FileZone type="img" old={old} documentId={documentId} onSuccess={onSuccess} />
    </div>
  );
};

const Pictures = () => {
  const LIST_TYPES = ['list', 'box'];
  const { documentId } = useParams();
  const queryClient = useQueryClient();
  const { data: doc } = useGetDocumentBySuperId(documentId);
  const css = useCss({ documentSuperId: documentId });
  const [listType, setListType] = useState(LIST_TYPES[1]);
  const [currentImage, setCurrentImage] = useState('');
  const [showImgInfo, setShowImgInfo] = useState(false);
  const [modalNewNameOpen, setModalNewNameOpen] = useState(false);
  const [errorImageName, setErrorImageName] = useState(false);
  const [imageName, setImageName] = useState('');
  const [imageExtension, setImageExtension] = useState('');
  const [imageToDelete, setImageToDelete] = useState(null);
  const [imageInUseError, setImageInUseError] = useState(false);
  const { mutate: changeImageName } = useChangeImageName();
  const { mutate: deleteImage, isLoading: isDeletingImage } = useDeleteImage();

  const imageRevisionIds = (doc?.content?.images ?? []).map((img) => img.revisionId);
  const { data: imageDataMap = {} } = useBatchImages(documentId, imageRevisionIds);

  const fullFileName = imageName + imageExtension;
  function validName(documentName) {
    return documentName.trim().length > 0 && documentName.trim().length < 65;
  }

  const imageMenuItems = (data) => [
    {
      text: i18n('pictures.dropdown.item.upload-new'),
      icon: 'fa-pen',
      sub: (item) => {
        return (
          <Tippy
            interactive={true}
            offset={[-130, -310]}
            trigger="click"
            content={<UploadFile old={data} onSuccess={handleUpdateSuccess} documentId={documentId} />}
          >
            {item}
          </Tippy>
        );
      },
    },
    {
      text: i18n('pictures.dropdown.item.edit-name'),
      icon: 'fa-pen',
      onClick: () => {
        const extension = path.extname(data.file.filename);
        const fileName = path.basename(data.file.filename, extension);

        setCurrentImage(data);
        setShowImgInfo(false);
        setImageName(fileName);
        setImageExtension(extension);
        setModalNewNameOpen(true);
      },
    },
    {
      text: i18n('pictures.dropdown.item.show-details'),
      icon: 'fa-info-circle',
      onClick: () => {
        setCurrentImage(data);
        setShowImgInfo(true);
      },
    },
    {
      text: i18n('pictures.dropdown.item.delete-picture'),
      icon: 'fa-trash',
      onClick: async () => {
        setImageToDelete(data.file.revisionId);
        setCurrentImage({ filename: data.file.filename });
        setImageInUseError(false);
      },
    },
  ];

  const handleConfirmDelete = async () => {
    deleteImage(
      { documentSuperId: documentId, imageRevisionId: imageToDelete },
      {
        onSuccess: () => {
          setShowImgInfo(false);
          setImageToDelete(null);
          reloadData();
          dispatchMessage(EventType.ADD_MESSAGE, {
            type: 'success',
            text: i18n('pictures.delete-success'),
            removeTime: 4000,
          });
        },
        onError: (error) => {
          const response = JSON.parse(error.request.response);
          if (response && response.message && response.message === 'image-in-use') {
            setImageInUseError(true);
          } else {
            dispatchMessage(EventType.ADD_MESSAGE, {
              type: 'error',
              text: i18n('pictures.delete-error'),
              removeTime: 4000,
            });
            ServerErrorHandler(error);
          }
        },
      }
    );
  };

  const DetailView = () => {
    const { file, metadata, occurences } = currentImage;
    const sectionPermissions = useSelector((state) => state.document.sectionPermissions);
    const { pDocument } = useEditorSelector((editor) => editor);
    const { mutate: createAccessLog } = useCreateAccessLog();
    const { documentPermissions } = usePermissions(documentId);

    return showImgInfo ? (
      <>
        <div className="detail-view pictures">
          <div className="close-info-btn" onClick={() => setShowImgInfo(false)}>
            <i className="fa-regular fa-xmark" />
          </div>
          <p className="title">{file.filename}</p>
          <div className="img-wrapper">
            <DataImage {...file} />
          </div>
          <div className="info mt-4">
            <div className="columns two mb-3">
              <p className="title">{i18n('pictures.details-view.type.title')}</p>
              <p>{file.fileType}</p>
            </div>
            <div className="columns two mb-3">
              <p className="title">{i18n('pictures.details-view.type.occurrence')}</p>
              <div className="flex flex-col">{formatImageOccurences(occurences, documentId, sectionPermissions, createAccessLog, pDocument.organization)}</div>
            </div>
            <div className="columns two mb-3">
              <p className="title">{i18n('pictures.details-view.type.filesize')}</p>
              {sizeWithVolume(metadata.size ?? 0)}
            </div>
            <div className="columns two mb-3">
              <p className="title">{i18n('pictures.details-view.title.uploaded-at')}</p>
              <p>{formatDate(file.createdAt)}</p>
            </div>
            {documentPermissions.includes('CODER') && (
              <div className="columns two mb-3">
                <p className="title">{i18n('pictures.details-view.title.super_id')}</p>
                <p>{file?.superId}</p>
              </div>
            )}
          </div>
        </div>
      </>
    ) : null;
  };

  const reloadData = () => {
    queryClient.invalidateQueries([documentQueryKeys.document, { documentSuperId: documentId }]);
  };

  const handleUpdateSuccess = async ({ data }, old) => {
    await request.post(`/gaby/documents/${documentId}/actions`, {
      action: 'update_image',
      revisionId: old.revisionId,
      data: {
        revisionId: data.revisionId,
        superId: data.superId,
      },
    });

    reloadData();
  };

  const handleCreateSuccess = async ({ data }) => {
    await request.post(`/gaby/documents/${documentId}/actions`, {
      action: 'create_image',
      data: {
        revisionId: data.revisionId,
        superId: data.superId,
      },
    });

    await reloadData();
  };

  const toggleListType = () => {
    setListType(listType === LIST_TYPES[0] ? LIST_TYPES[1] : LIST_TYPES[0]);
  };

  const handleImageNameUpdate = (event) => {
    setImageName(event.target.value);
  };

  function handleClose() {
    setErrorImageName(false);
    setModalNewNameOpen(false);
  }

  const handleImageNameChangeSubmit = async (event) => {
    event.preventDefault();
    if (!validName(fullFileName)) {
      setErrorImageName(true);
      return;
    }

    // Proceed with the API call to change the name
    changeImageName({
      documentSuperId: currentImage.file.documentSuperId,
      imageSuperId: currentImage.file.superId,
      imageRevisionId: currentImage.file.revisionId,
      newName: fullFileName,
    });

    setErrorImageName(false);
    setModalNewNameOpen(false);
  };
  const ImageList = () => {
    const images = (doc.content.images ?? []).map((image) => (
      <Picture
        listType={listType}
        imageData={imageDataMap[image.revisionId]}
        key={image.revisionId}
        imageMenuItems={imageMenuItems}
        documentSuperId={documentId}
      />
    ));

    switch (listType) {
      case 'box':
        return (
          <div className="image--list">
            <div className="columns five gutter-12">{images}</div>
          </div>
        );
      case 'list':
      default:
        return (
          <table>
            <thead>
              <tr>
                <th></th>
                <th>{i18n('pictures.list-selector.list.thead.page')}</th>
                <th>{i18n('pictures.list-selector.list.thead.name')}</th>
                <th>{i18n('pictures.list-selector.list.thead.content-size')}</th>
                <th>{i18n('pictures.list-selector.list.thead.uploaded-at')}</th>
                <th>{i18n('pictures.list-selector.list.thead.actions')}</th>
              </tr>
            </thead>
            <tbody>{images}</tbody>
          </table>
        );
    }
  };

  return (
    <div>
      <style type="text/css">{css.data?.exposedCss}</style>
      <DetailView />
      <div className="overview--image">
        <div className="overview--image--menu">
          <button className="fab clear justify-self-end" onClick={toggleListType}>
            <i className="fa-regular fa-list" />
          </button>
          <div className="divider vertical" />
          <Tippy
            interactive={true}
            offset={[-130, 10]}
            placement="bottom"
            trigger="click"
            content={<UploadFile onSuccess={handleCreateSuccess} documentId={documentId} />}
          >
            <button className="fab">
              <i className="fa-regular fa-plus font-15" />
            </button>
          </Tippy>
        </div>
        <div className="overview--image--content">{doc ? <ImageList /> : <Spinner />}</div>
      </div>
      <Modal show={modalNewNameOpen}>
        <div className="header d-flex">
          <h3>{i18n('project-list.new-name.modal.title')}</h3>
          <div
            className="close"
            onClick={() => {
              handleClose();
            }}
          >
            <i className="fa-regular fa-xmark" />
          </div>
        </div>
        <div className="body ">
          <form onSubmit={handleImageNameChangeSubmit} className={errorImageName ? 'form error' : 'form'}>
            <div className="mt-4">
              <label>
                <input type="text" ref={(o) => o?.focus()} value={imageName} placeholder="" onChange={handleImageNameUpdate} />
              </label>
              <span className="text-help">{errorImageName ? i18n('project-list.new-name.field.nameError') : ''}</span>
            </div>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="button clear"
                onClick={() => {
                  handleClose();
                }}
              >
                {i18n('common.button.cancel')}
              </button>
              <button type="submit" className="button primary">
                {i18n('common.button.save')}
              </button>
            </div>
          </form>
        </div>
      </Modal>
      <ConfirmationModal
        message={
          <>
            <i className="fa-regular fa-trash-can fs-1 mb-4" />
            <h3 className="confirm-delete-header">
              {i18n('pictures.confirm-delete')}: {currentImage.filename}
            </h3>
            {isDeletingImage && <Spinner className="mt-3" />}
            <span className="text-gray-900 fs-7">{i18n('pictures.confirm-delete-info')}</span>
            {imageInUseError && <div className="text-red-500">{i18n('pictures.confirm-delete.image-in-use-error')}</div>}
          </>
        }
        title={' '}
        show={!!imageToDelete}
        onConfirm={handleConfirmDelete}
        onDecline={() => setImageToDelete(null)}
      />
    </div>
  );
};

export default Pictures;
